import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getCountrySpecificSEO } from '../config/seo';
import { Country } from '../types';

interface SEOState {
  title: string;
  description: string;
  keywords: string[];
  canonical: string;
  image: string;
}

interface UseSEOOptions {
  country?: Country | null;
  state?: 'default' | 'processing' | 'editor' | 'ready' | 'selecting';
  noIndex?: boolean;
  customTitle?: string;
  customDescription?: string;
}

export const useSEO = ({
  country = null,
  state = 'default',
  noIndex = false,
  customTitle,
  customDescription
}: UseSEOOptions = {}): SEOState & { noIndex: boolean; language: string; country: Country | null } => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language || 'en';

  return useMemo(() => {
    const seoData = getCountrySpecificSEO(country, state, currentLanguage);
    
    return {
      title: customTitle || seoData.title,
      description: customDescription || seoData.description,
      keywords: seoData.keywords,
      canonical: seoData.canonical,
      image: seoData.image,
      noIndex,
      language: currentLanguage,
      country
    };
  }, [country, state, currentLanguage, customTitle, customDescription, noIndex]);
};

// Specialized hooks for different application states
export const useDefaultSEO = (country?: Country | null) => {
  return useSEO({ country, state: 'default' });
};

export const useProcessingSEO = (country: Country) => {
  return useSEO({ country, state: 'processing' });
};

export const useEditorSEO = (country: Country) => {
  return useSEO({ country, state: 'editor' });
};

export const useReadySEO = (country: Country) => {
  return useSEO({ country, state: 'ready' });
};

export const useSelectingSEO = (country: Country) => {
  return useSEO({ country, state: 'selecting' });
};